import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Loader2, Pencil, Plus, Save, Trash2, X } from 'lucide-react';

import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { packagingService } from '@/services/inventory';
import type {
  DimensionUnit,
  FulfilmentDefaultsInput,
  PackagingBox,
  PackagingBoxInput,
  WeightUnit,
} from '@/types/inventory';

const EMPTY_BOX: PackagingBoxInput = {
  name: '',
  lengthCm: 0,
  widthCm: 0,
  heightCm: 0,
  maxWeightGrams: 0,
  tareWeightGrams: 0,
};

export function PackagingPage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Packaging & fulfilment"
        description="Org-level shipping defaults and the box catalogue dispatch auto-selects from."
      />
      <DefaultsCard />
      <BoxesCard />
    </div>
  );
}

// ── Fulfilment defaults ───────────────────────────────────────────────
function DefaultsCard() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['inventory', 'fulfilment-defaults'],
    queryFn: () => packagingService.getDefaults(),
  });
  const [form, setForm] = useState<FulfilmentDefaultsInput>({});

  useEffect(() => {
    if (query.data) {
      setForm({
        fallbackUnitWeightGrams: query.data.fallbackUnitWeightGrams,
        volumetricDivisor: query.data.volumetricDivisor,
        defaultWeightUnit: query.data.defaultWeightUnit,
        defaultDimensionUnit: query.data.defaultDimensionUnit,
      });
    }
  }, [query.data]);

  const save = useMutation({
    mutationFn: () => packagingService.updateDefaults(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'fulfilment-defaults'] });
      toast.success('Fulfilment defaults saved');
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Could not save'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[15px]">Fulfilment defaults</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-[12px] text-muted-foreground">
          Applied when an item has no value of its own — set these once instead of on every item.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Fallback weight per unit (g)" hint="When an item has no weight">
            <Input
              type="number" min={0}
              value={form.fallbackUnitWeightGrams ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, fallbackUnitWeightGrams: Number(e.target.value) }))}
            />
          </Field>
          <Field label="Volumetric divisor" hint="cm³/kg — 5000 for air express">
            <Input
              type="number" min={1000}
              value={form.volumetricDivisor ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, volumetricDivisor: Number(e.target.value) }))}
            />
          </Field>
          <Field label="Default weight unit">
            <Select
              value={form.defaultWeightUnit ?? 'KG'}
              onValueChange={(v) => setForm((f) => ({ ...f, defaultWeightUnit: v as WeightUnit }))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['KG', 'G', 'LB', 'OZ'].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Default dimension unit">
            <Select
              value={form.defaultDimensionUnit ?? 'CM'}
              onValueChange={(v) => setForm((f) => ({ ...f, defaultDimensionUnit: v as DimensionUnit }))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['CM', 'IN', 'M', 'MM'].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div>
          <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save defaults
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Box catalogue ─────────────────────────────────────────────────────
function BoxesCard() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['inventory', 'packaging-boxes'],
    queryFn: () => packagingService.listBoxes(),
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PackagingBoxInput>(EMPTY_BOX);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['inventory', 'packaging-boxes'] });

  const save = useMutation({
    mutationFn: () =>
      editingId
        ? packagingService.updateBox(editingId, form)
        : packagingService.createBox(form),
    onSuccess: () => {
      invalidate();
      toast.success(editingId ? 'Box updated' : 'Box added');
      setEditingId(null);
      setForm(EMPTY_BOX);
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Could not save'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => packagingService.removeBox(id),
    onSuccess: () => { invalidate(); toast.success('Box deleted'); },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Could not delete'),
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => packagingService.reorderBoxes(ids),
    onSuccess: () => invalidate(),
  });

  const boxes = query.data ?? [];
  const startEdit = (b: PackagingBox) => {
    setEditingId(b.id);
    setForm({
      name: b.name, lengthCm: b.lengthCm, widthCm: b.widthCm, heightCm: b.heightCm,
      maxWeightGrams: b.maxWeightGrams, tareWeightGrams: b.tareWeightGrams, enabled: b.enabled,
    });
  };
  const move = (idx: number, dir: -1 | 1) => {
    const ids = boxes.map((b) => b.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    reorder.mutate(ids);
  };
  const canSave = form.name.trim() && form.lengthCm > 0 && form.widthCm > 0 && form.heightCm > 0 && form.maxWeightGrams > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[15px]">Boxes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-[12px] text-muted-foreground">
          Dispatch auto-picks the smallest box whose volume + max weight fit the order; its
          dimensions + empty-box weight are sent to the carrier.
        </p>

        {query.isLoading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
        ) : boxes.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">No boxes yet — add one below.</p>
        ) : (
          <div className="overflow-hidden rounded-md border border-border/60">
            <table className="w-full text-[12.5px]">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Box</th>
                  <th className="px-3 py-2 text-left">L×W×H (cm)</th>
                  <th className="px-3 py-2 text-left">Max</th>
                  <th className="px-3 py-2 text-left">Tare</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {boxes.map((b, idx) => (
                  <tr key={b.id} className={b.enabled ? '' : 'opacity-50'}>
                    <td className="px-3 py-2 font-medium text-foreground">
                      {b.name}{!b.enabled && ' (disabled)'}
                    </td>
                    <td className="px-3 py-2 font-mono">{b.lengthCm}×{b.widthCm}×{b.heightCm}</td>
                    <td className="px-3 py-2">{(b.maxWeightGrams / 1000).toFixed(2)} kg</td>
                    <td className="px-3 py-2">{b.tareWeightGrams} g</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-0.5">
                        <IconBtn label="Up" disabled={idx === 0 || reorder.isPending} onClick={() => move(idx, -1)}><ChevronUp className="h-3.5 w-3.5" /></IconBtn>
                        <IconBtn label="Down" disabled={idx === boxes.length - 1 || reorder.isPending} onClick={() => move(idx, 1)}><ChevronDown className="h-3.5 w-3.5" /></IconBtn>
                        <IconBtn label="Edit" onClick={() => startEdit(b)}><Pencil className="h-3.5 w-3.5" /></IconBtn>
                        <IconBtn label="Delete" danger disabled={remove.isPending} onClick={() => remove.mutate(b.id)}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add / edit form */}
        <div className="rounded-md border border-border/60 bg-muted/30 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-medium text-foreground">{editingId ? 'Edit box' : 'Add box'}</span>
            {editingId && (
              <button type="button" className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" onClick={() => { setEditingId(null); setForm(EMPTY_BOX); }}>
                <X className="h-3 w-3" /> Cancel
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Name" className="sm:col-span-2">
              <Input value={form.name} placeholder="Medium carton" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </Field>
            <Field label="Length (cm)"><Input type="number" min={1} value={form.lengthCm || ''} onChange={(e) => setForm((f) => ({ ...f, lengthCm: Number(e.target.value) }))} /></Field>
            <Field label="Width (cm)"><Input type="number" min={1} value={form.widthCm || ''} onChange={(e) => setForm((f) => ({ ...f, widthCm: Number(e.target.value) }))} /></Field>
            <Field label="Height (cm)"><Input type="number" min={1} value={form.heightCm || ''} onChange={(e) => setForm((f) => ({ ...f, heightCm: Number(e.target.value) }))} /></Field>
            <Field label="Max weight (kg)">
              <Input type="number" min={0} step="0.1"
                value={form.maxWeightGrams ? form.maxWeightGrams / 1000 : ''}
                onChange={(e) => setForm((f) => ({ ...f, maxWeightGrams: Math.round(Number(e.target.value) * 1000) }))} />
            </Field>
            <Field label="Empty box weight (g)">
              <Input type="number" min={0} value={form.tareWeightGrams ?? 0} onChange={(e) => setForm((f) => ({ ...f, tareWeightGrams: Number(e.target.value) }))} />
            </Field>
          </div>
          <div className="mt-3 flex justify-end">
            <Button size="sm" disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {editingId ? 'Save box' : 'Add box'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, hint, className, children }: { label: string; hint?: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <Label className="mb-1 block text-[11px]">{label}</Label>
      {children}
      {hint && <div className="mt-1 text-[10.5px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function IconBtn({ label, onClick, disabled, danger, children }: { label: string; onClick: () => void; disabled?: boolean; danger?: boolean; children: React.ReactNode }) {
  return (
    <Button type="button" size="icon" variant="ghost" title={label} aria-label={label} disabled={disabled} onClick={onClick}
      className={danger ? 'h-7 w-7 text-destructive hover:text-destructive' : 'h-7 w-7'}>
      {children}
    </Button>
  );
}
